/**
 * A generic cache class that implements a reference counting cached indexed by tile coordinates. This implements the
 * generic machinery for caching items by tile coordinates, managing a reference count and triggering resource
 * destruction once the reference count reaches zero.
 *
 * Classes can derive from this, or it can be used directly to provide a cache for a specific purpose. The primary piece
 * of functionality to override is the 'onDestruction' callback, which will be called when an item has reached zero
 * references and should be destroyed.
 */
export class TCache {
    /**
     * @param tilingScheme The tiling scheme this cache is storing items in.
     */
    constructor(tilingScheme) {
        this.cache = [];
        this.tilingScheme = tilingScheme;
        // 32 zoom levels as we'll likely never get data refined to a higher level than that
        for (let i = 0; i < 32; i += 1) {
            this.cache.push(new Map());
        }
    }
    /**
     * Writes a new entry into the cache for the specified tile. This will overwrite the old entry regardless of the
     * existing entry's reference count.
     *
     * Calling this on an already cached tile will replace the old item, leaving it dangling. The reference count will
     * no longer be managed and so it will never have 'onDestruction' called for it. Freeing dangling tiles is the
     * caller's responsibility.
     *
     * In general this function should not be called for already tiled items.
     *
     * @param x The tile x coordinate to add the item for
     * @param y The tile y coordinate to add the item for
     * @param zoomLevel The zoom level to add the item for
     * @param item The item to add to the cache
     */
    addItem(x, y, zoomLevel, item) {
        const hash = this.hashTileCoordinateForNode(x, y, zoomLevel);
        const cacheItem = {
            refCount: 1,
            v: item,
        };
        this.cache[zoomLevel].set(hash, cacheItem);
        return cacheItem.v;
    }
    /**
     * Attempts to acquire a cached item for the specified tile. Will return 'undefined' if no item is cached for that
     * tile. If the item *is* cached then the reference count will be incremented and a reference to the item will be
     * returned.
     *
     * To correctly use the cache there should be exactly one {@link TCache.release} call for every 'acquire' call. If
     * 'acquire' is called more times than {@link TCache.release} then the item's reference count will never reach zero,
     * and the item will be left dangling.
     *
     * {@link TCache.release} being called more times than 'release' won't trigger immediate mis-behavior.
     *
     * @see TCache.release
     *
     * @param x
     * @param y
     * @param zoomLevel
     */
    acquire(x, y, zoomLevel) {
        const hash = this.hashTileCoordinateForNode(x, y, zoomLevel);
        const cache = this.cache[zoomLevel];
        const item = cache.get(hash);
        // Increment the reference count as we're handing out another reference.
        if (item !== undefined) {
            item.refCount += 1;
        }
        return item === null || item === void 0 ? void 0 : item.v;
    }
    /**
     * Attempts to release the item for the specified tile. If there is a cached item for the requested tile then the
     * reference count will be decremented. If the reference count reaches zero then 'onDestruction' will be called with
     * item in question as the parameter.
     *
     * 'onDestruction' should be some function or closure that will manage releasing the resources associated with the
     * cached item.
     *
     * If 'release' is called for an item that is not cached then it will simply do nothing.
     *
     * To correctly use the cache there should be exactly one 'release' call for every {@link TCache.acquire} call. If
     * {@link TCache.acquire} is called more times than 'release' then the item's reference count will never reach zero,
     * and the item will be left dangling.
     *
     * If a user attempts to call 'release' more times than {@link TCache.acquire} for a particular resource then no
     * immediate problems will occur. The best case is that nothing happens as 'release' is called for an uncached item
     * and does nothing. Problems can happen if a cache slot is reused though. In this case we may be trying to call
     * 'release' for a resource that has already been destroyed and replaced by a new item, releasing a resource we
     * never owned.
     *
     * @see TCache.acquire
     *
     * @param x
     * @param y
     * @param zoomLevel
     * @param onDestruction
     */
    release(x, y, zoomLevel, onDestruction) {
        const hash = this.hashTileCoordinateForNode(x, y, zoomLevel);
        const cache = this.cache[zoomLevel];
        const item = cache.get(hash);
        if (item !== undefined) {
            // Decrement the reference count. Once it reaches zero we can evict the geometry
            item.refCount -= 1;
            if (item.refCount <= 0) {
                onDestruction(item.v);
                cache.delete(hash);
            }
        }
    }
    /**
     * This provides a simple and efficient hash function for a zoomLevel + tileCoordinate pair that will provide a
     * unique integer for any coordinate at a given zoom level.
     *
     * All inputs *MUST* be integers. No guarantees on the output are made if *any* of the input values are not whole
     * numbers.
     *
     * This hash function is intended to be used for the node cache.
     *
     * WARNING
     *
     * There are no uniqueness guarantees *between zoom levels*. hashTileCoordinate(1, 1, 0) and
     * hashTileCoordinate(2, 1, 0) *WILL* hash to the same value. The uniqueness of the hash is only held at the same
     * zoom level.
     * @param zoomLevel The zoom level we're hashing for
     * @param x The x coordinate of the tile (horizontal)
     * @param y The y coordinate of the tile (vertical)
     */
    hashTileCoordinateForNode(x, y, zoomLevel) {
        const width = this.tilingScheme.getNumberOfXTilesAtLevel(zoomLevel);
        return width * y + x;
    }
}
